import * as React from 'react';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
Chart.register(ArcElement, Tooltip, Legend);

interface Props {
  labels: string[];
  dataSets: Array<{ data: number[]; backgroundColor: string[]; labels?: string[] }>;
  licenseTypeTexts: string[];
}

const buildData = (labels: string[], dataSets: Array<any>, licenseTypeTexts: string[]) => ({
  labels,
  datasets: dataSets,
  licenseTypeTexts
});

const baseOptions: any = {
  responsive: false,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx: any) => {
          const i = ctx.dataIndex;
          const raw = ctx.chart.config.data.licenseTypeTexts?.[i];
          return raw ? raw.split('\n').filter((x: string) => x) : '';
        }
      }
    }
  },
  animation: false,
  cutout: '79%'
};

// Prefer a balanced 2-line split near an ideal length; fallback to greedy wrap.
function wrapCompanyName(text: string, idealLen = 18, hardMax = 22, maxLines = 3) {
  const name = (text || '').trim();
  if (!name) return [];
  const words = name.split(/\s+/);
  if (words.length > 1) {
    let best: { idx: number; score: number } | null = null;
    for (let k = 1; k < words.length; k++) {
      const l1 = words.slice(0, k).join(' ');
      const l2 = words.slice(k).join(' ');
      if (l1.length <= hardMax && l2.length <= hardMax) {
        const score = Math.abs(idealLen - l1.length) + Math.abs(idealLen - l2.length);
        if (!best || score < best.score) best = { idx: k, score };
      }
    }
    if (best) {
      return [words.slice(0, best.idx).join(' '), words.slice(best.idx).join(' ')];
    }
  }
  // Greedy wrap as a fallback
  const lines: string[] = [];
  let current = '';
  for (const w of words) {
    // If a single word exceeds hardMax, hard-split it
    if (w.length > hardMax) {
      if (current) { lines.push(current); current = ''; }
      for (let i = 0; i < w.length; i += hardMax) {
        lines.push(w.slice(i, i + hardMax));
        if (lines.length >= maxLines) return lines;
      }
      continue;
    }
    const next = current ? current + ' ' + w : w;
    if (next.length <= hardMax) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = w;
      if (lines.length >= maxLines) return lines;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  return lines;
}
function drawCenteredLines(ctx: CanvasRenderingContext2D, width: number, height: number, lines: string[]) {
  const x = (t: string) => (width - ctx.measureText(t).width) / 2;
  switch (lines.length) {
    case 1:
      ctx.fillText(lines[0], x(lines[0]), height / 1.9);
      break;
    case 2:
      ctx.fillText(lines[0], x(lines[0]), height / 2.2);
      ctx.fillText(lines[1], x(lines[1]), height / 1.8);
      break;
    case 3:
      ctx.fillText(lines[0], x(lines[0]), height / 2.5);
      ctx.fillText(lines[1], x(lines[1]), height / 1.9);
      ctx.fillText(lines[2], x(lines[2]), height / 1.5);
      break;
    case 4:
      ctx.fillText(lines[0], x(lines[0]), height / 3.0);
      ctx.fillText(lines[1], x(lines[1]), height / 2.0);
      ctx.fillText(lines[2], x(lines[2]), height / 1.5);
      ctx.fillText(lines[3], x(lines[3]), height / 1.2);
      break;
    default:
      if (lines[0]) ctx.fillText(lines[0], x(lines[0]), height / 2);
  }
}
// Plugin: draw centered text
// - Default: "Types of" + "Licenses"
// - On hover: show hovered slice label in center
const centerTextPlugin = {
  id: 'centerTextPlugin',
  afterDraw(chart: any) {
    const { ctx, width, height } = chart;
    ctx.save();
    ctx.font = "14px Roboto Light, 'Open Sans', Arial, sans-serif";
    ctx.fillStyle = '#000';
    const active = chart.getActiveElements ? chart.getActiveElements() : [];
    if (active && active.length > 0) {
      const idx = active[0].index;
      const ds = chart.data.datasets && chart.data.datasets[0] ? chart.data.datasets[0] : undefined;
      const name = (ds && ds.labels && ds.labels[idx]) || (chart.data.labels && chart.data.labels[idx]) || '';
      const count = ds && ds.data ? ds.data[idx] : undefined;
      if (name) {
        const nameLines = wrapCompanyName(name, 18, 22, 3);
        const lines = [...nameLines];
        if (count !== undefined) {
          lines.push(String(count));
        }
        drawCenteredLines(ctx, width, height, lines);
        ctx.restore();
        return;
      }
    }
    const text1 = 'Types of';
    const text2 = 'Licenses';
    const x1 = (width - ctx.measureText(text1).width) / 2;
    const x2 = (width - ctx.measureText(text2).width) / 2;
    ctx.fillText(text1, x1, height / 2.2);
    ctx.fillText(text2, x2, height / 1.8);
    ctx.restore();
  }
};
class LicenseDoughnutChart extends React.Component<Props> {
  private chartRef = React.createRef<HTMLCanvasElement>();
  private chartInstance: Chart | null = null;

  componentDidMount() { this.scheduleRenderChart(); }

  componentDidUpdate(prev: Props) {
    if (prev.labels !== this.props.labels || prev.dataSets !== this.props.dataSets) {
      this.scheduleRenderChart();
    }
  }

  componentWillUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }

  scheduleRenderChart() {
    const sliceCount = this.props.dataSets?.[0]?.data?.length ?? 0;
    const run = () => this.renderChart();
    if (sliceCount > 100 && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      run();
    }
  }

  renderChart() {
    if (!this.chartRef.current) return;
    const data = buildData(this.props.labels, this.props.dataSets, this.props.licenseTypeTexts);
    if (this.chartInstance) { this.chartInstance.destroy(); }
    this.chartInstance = new Chart(this.chartRef.current, {
      type: 'doughnut',
      data,
      options: baseOptions,
      plugins: [centerTextPlugin]
    });
  }
  render() { return <canvas ref={this.chartRef} height={300} style={{ height: 300 }} />; }
}
export default LicenseDoughnutChart;
